import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import ListView from "views/common/list";
import React from "react";
import BankTransferViewModel from "viewModels/finance/outgoingPayments/bankTransferViewModel";
import type OutgoingTransferPaymentResponse from "entities/outgoingTransferPaymentResponse";
import { formatAmount, formatDate, formatLicensePlate } from "utils/localizations";
import Button from "@edaz/edazkit/Button/index";
import BankTransferFilters from "./bankTransferFilters";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import { emptyValue } from "utils/emptyValue";

const BankTransferView: ViewComponent<BankTransferViewModel> = observer(({ vm }) => {
  const onItemClick = vm.onItemClick();

  const columns: ColumnDefinition<OutgoingTransferPaymentResponse>[] = [
    {
      title: vm.translate("licensePlate"),
      valueFormatter: props => {
        const vehicle = props.item.searchedCharge?.vehicle;
        return vehicle ? formatLicensePlate(vehicle) : emptyValue;
      },
    },
    {
      property: "recipientBankAccount",
      title: vm.translateAttribute("recipientBankAccount"),
      cellProps: props => ({ style: { whiteSpace: "pre-line" } }),
      valueFormatter: props => formatBankAccount(props.value),
    },
    {
      property: "variableSymbol",
      title: vm.translate("variableSymbolOutgoingPayment"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "paidAmount",
      title: vm.translateAttribute("paidAmount"),
      valueFormatter: props => formatAmount(props.value),
    },
    {
      property: "postingDate",
      title: vm.translateAttribute("postingDate"),
      valueFormatter: props => formatDate(props.value) || emptyValue,
    },
    {
      property: "exportedDate",
      title: vm.translateAttribute("exportedDate"),
      valueFormatter: props => formatDate(props.value),
    },
    {
      title: vm.translate("officialRecordId"),
      valueFormatter: ({ item }) => item.searchedCharge?.officialRecordId ?? emptyValue,
    },
    {
      property: "status",
      title: vm.translateAttribute("status"),
      valueFormatter: props => vm.translateOutgoingTransferPaymentStatus(props.value),
    },
    {
      property: "messageForRecipient",
      title: vm.translateAttribute("messageForRecipient"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      title: vm.translate("chargeOrderId"),
      valueFormatter: ({ item }) => item.searchedCharge?.chargeOrderId ?? emptyValue,
    },
    {
      title: vm.translate("chargeId"),
      valueFormatter: ({ item }) => item.searchedCharge?.chargeId ?? emptyValue,
    },
  ];

  let actionButtons;
  if (onItemClick) {
    actionButtons = (item: OutgoingTransferPaymentResponse) => (
      <Button size="sm" className="rounded btn btn-light btn-sm" onClick={() => onItemClick(item)}>
        {vm.translate("detail")}
      </Button>
    );
  }

  return (
    <>
      <BankTransferFilters vm={vm} />
      <ListView
        showDetailWithList={true}
        vm={vm}
        columns={columns}
        hideTitle={true}
        mandatoryFilter={true}
        actionButtons={actionButtons}
      />
    </>
  );
});

registerView(BankTransferView, BankTransferViewModel);
